import { useState } from "react";
import { useTranslation } from "react-i18next";
import { fmtTime, scriptCostSeconds } from "../../lib/api";
import { Sparkles, Loader2, CheckCircle2, Lightbulb, ArrowRight } from "lucide-react";
import { CostBadge } from "../ui-bits";
import { useUploads } from "../../context/UploadContext";

export default function QualityPanel({ quality, projectId, onImproving, totalWords = 0, onContinue, improving = false, improveCount = 0 }) {
  const { t } = useTranslation();
  const { startScriptImprove } = useUploads();
  const [busy, setBusy] = useState(false);
  const tier = (n) =>
    n >= 85 ? { label: t("quality.tierExcellent"), color: "#16a34a", bar: "bg-emerald-500", text: "text-emerald-700" }
    : n >= 70 ? { label: t("quality.tierGood"), color: "#2563eb", bar: "bg-blue-600", text: "text-blue-700" }
    : n >= 50 ? { label: t("quality.tierAcceptable"), color: "#d97706", bar: "bg-amber-500", text: "text-amber-700" }
    : { label: t("quality.tierImprovable"), color: "#dc2626", bar: "bg-red-500", text: "text-red-700" };
  if (!quality) return null;
  const tr = tier(quality.score);
  const minutes = Math.max(10, Math.min(40, Math.round(totalWords / 145) || 20));
  const improveCost = scriptCostSeconds(minutes);
  const isImproving = busy || improving;

  const improve = async () => {
    if (!window.confirm(t("quality.confirmImprove"))) return;
    setBusy(true);
    const ok = await startScriptImprove(projectId);
    setBusy(false);
    if (ok) onImproving && onImproving();
  };

  return (
    <div className="space-y-1" data-testid="quality-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <button data-testid="improve-script-button" onClick={improve} disabled={isImproving}
          className="min-h-[48px] text-base font-bold rounded-xl bg-amber-500 text-white hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 px-4 sm:px-5 disabled:opacity-60">
          {isImproving ? <Loader2 className="animate-spin" size={20} /> : <Sparkles size={20} />}
          {isImproving ? t("quality.improving") : t("quality.improveWithAi")}
          {!isImproving && <CostBadge seconds={improveCost} tip={t("quality.improveTip")} />}
        </button>
        <button data-testid="skip-improve-button" onClick={onContinue} disabled={isImproving}
          className="min-h-[48px] text-base font-bold rounded-xl border-2 border-slate-300 text-slate-700 hover:border-blue-600 hover:text-blue-700 transition-colors flex items-center justify-center gap-2 px-4 sm:px-5 disabled:opacity-60">
          {t("quality.skip")} <ArrowRight size={18} />
        </button>
      </div>
      {improveCount > 0 && !isImproving && (
        <div data-testid="already-improved-note" className="flex items-start gap-2 -mt-2 mb-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <Lightbulb size={16} className="shrink-0 mt-0.5" />
          <span>{t("quality.alreadyImprovedNote")}</span>
        </div>
      )}
      <div className="flex items-end justify-between mb-1">
        <span className="text-sm font-semibold text-slate-500">{t("quality.scoreLabel")}</span>
        <span className={`text-2xl font-bold ${tr.text}`} data-testid="quality-score">
          {quality.previous_score != null && (
            <span className="text-base font-semibold text-slate-400 mr-1.5" data-testid="quality-previous-score">{quality.previous_score} →</span>
          )}
          {quality.score}/100
        </span>
      </div>
      <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${tr.bar} transition-all duration-700`} style={{ width: `${quality.score}%` }} />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className={`text-sm font-bold ${tr.text}`}>{tr.label}</span>
      </div>
      {quality.resumen && <p className="text-base text-slate-700 mt-3">{quality.resumen}</p>}

      {quality.criterios?.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="quality-criteria">
          {quality.criterios.map((c, i) => {
            const ct = tier(c.nota);
            return (
              <div key={i}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-700">{t(`quality.crit.${c.clave}`, { defaultValue: c.clave })}</span>
                  <span className={`font-bold ${ct.text}`}>{c.nota}</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
                  <div className={`h-full ${ct.bar}`} style={{ width: `${c.nota}%` }} />
                </div>
                {c.comentario && <p className="text-xs text-slate-500 mt-1">{c.comentario}</p>}
                {c.evidencia?.length > 0 && (
                  <p className="text-xs italic text-slate-400 mt-0.5" data-testid={`criteria-evidence-${c.clave}`}>
                    “{c.evidencia.join("” · “")}”
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {quality.mejoras?.length > 0 && (
        <div className="mt-5 bg-amber-50 border-2 border-amber-200 rounded-xl p-4" data-testid="quality-improvements">
          <div className="flex items-center gap-2 mb-2 text-amber-800 font-bold text-base"><Lightbulb size={18} /> {t("quality.suggestedImprovements")}</div>
          <ul className="space-y-1.5">
            {quality.mejoras.map((m, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><CheckCircle2 size={16} className="text-amber-600 mt-0.5 shrink-0" /> {m}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
